import * as Sentry from '@sentry/react';
import { createBrowserHistory } from 'history';
import { matchPath } from 'react-router-dom';
import { allFlattenRoutes } from './routes';

export const history = createBrowserHistory();

const sentryRoutes = allFlattenRoutes
    .filter((route) => route.path)
    .map((route) => ({
        path: route.path,
        exact: route.exact,
    }));

const getTracesSampleRate = () => {
    const sampleRate = Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE);
    return Number.isFinite(sampleRate) ? sampleRate : 0.1;
};

const levelPriority = {
    debug: 10,
    info: 20,
    log: 20,
    warning: 30,
    error: 40,
    fatal: 50,
};

const getMinimumEventLevel = () => {
    const configuredLevel = process.env.REACT_APP_SENTRY_MIN_LEVEL;
    return levelPriority[configuredLevel] ? configuredLevel : 'warning';
};

const isBelowMinimumLevel = (level) => {
    if (!level) {
        return false;
    }

    const eventPriority = levelPriority[level] || levelPriority.info;
    const minimumPriority = levelPriority[getMinimumEventLevel()];
    return eventPriority < minimumPriority;
};

export const initSentry = () => {
    const dsn = process.env.REACT_APP_SENTRY_DSN;

    if (!dsn) {
        return;
    }

    Sentry.init({
        dsn,
        environment: process.env.REACT_APP_SENTRY_ENV || process.env.REACT_APP_ENV || process.env.NODE_ENV,
        release: process.env.REACT_APP_SENTRY_RELEASE,
        integrations: [
            Sentry.reactRouterV5BrowserTracingIntegration({
                history,
                routes: sentryRoutes,
                matchPath,
            }),
        ],
        tracesSampleRate: getTracesSampleRate(),
        beforeSend(event) {
            if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_SENTRY_ENABLE_DEV !== 'true') {
                return null;
            }

            if (isBelowMinimumLevel(event.level)) {
                return null;
            }

            return event;
        },
    });
};

export const captureSentryException = (error, context = {}) => {
    Sentry.withScope((scope) => {
        scope.setLevel(context.level || 'error');

        if (context.tags) {
            Object.entries(context.tags).forEach(([key, value]) => scope.setTag(key, value));
        }

        if (context.extra) {
            Object.entries(context.extra).forEach(([key, value]) => scope.setExtra(key, value));
        }

        Sentry.captureException(error);
    });
};

export const captureSentryMessage = (message, context = {}) => {
    const level = context.level || 'warning';

    Sentry.withScope((scope) => {
        scope.setLevel(level);

        if (context.tags) {
            Object.entries(context.tags).forEach(([key, value]) => scope.setTag(key, value));
        }

        if (context.extra) {
            Object.entries(context.extra).forEach(([key, value]) => scope.setExtra(key, value));
        }

        Sentry.captureMessage(message, level);
    });
};
